import { Link } from 'react-router-dom'

interface ErrorStateProps {
  type: 'NOT_FOUND' | 'UNKNOWN'
  slug?: string
}

/**
 * Error state for Web Frame
 * Modern branded error pages
 */
export function ErrorState({ type, slug }: ErrorStateProps) {
  if (type === 'NOT_FOUND') {
    return <NotFoundError slug={slug} />
  }

  return <GenericError />
}

function NotFoundError({ slug }: { slug?: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
      {/* Content card */}
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-8 text-center animate-fade-in">
        {/* Avatar - use object-contain to preserve aspect ratio */}
        <div className="mb-6 flex justify-center">
          <img 
            src="/quapp_avtr_2.png" 
            alt="" 
            className="w-32 h-auto object-contain animate-bounce-soft"
          />
        </div>

        {/* Message */}
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Quapp Not Found
        </h1>
        <p className="text-slate-500 mb-4">
          We couldn't find a Quapp with this address
        </p>
        
        {slug && (
          <div className="bg-slate-100 rounded-xl px-4 py-3 mb-6">
            <code className="text-sm text-slate-600 break-all">{slug}</code>
          </div>
        )}

        <p className="text-slate-400 text-sm mb-8">
          The Quapp may have been removed, or the URL might be incorrect.
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <Link 
            to="/" 
            className="block w-full py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors"
          >
            Browse Quapps
          </Link>
          <button
            onClick={() => window.history.back()}
            className="block w-full py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
          >
            Go Back
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <span>Powered by</span>
            <img src="/quapp.png" alt="Quapp" className="h-5 object-contain" />
          </div>
        </div>
      </div>
    </div>
  )
}

function GenericError() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
      {/* Content card */}
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-8 text-center animate-fade-in">
        {/* Avatar - use object-contain to preserve aspect ratio */}
        <div className="mb-6 flex justify-center">
          <img 
            src="/quapp_avtr_1.png" 
            alt="" 
            className="w-32 h-auto object-contain"
          />
        </div>

        {/* Message */}
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Something Went Wrong
        </h1>
        <p className="text-slate-500 mb-8">
          We're having trouble loading this Quapp. This might be a temporary issue.
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <button 
            onClick={() => window.location.reload()}
            className="block w-full py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors"
          >
            Try Again
          </button>
          <Link 
            to="/" 
            className="block w-full py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
          >
            Browse Quapps
          </Link>
        </div>

        {/* Help text */}
        <p className="mt-6 text-slate-400 text-xs">
          If this keeps happening, the app might be temporarily unavailable.
        </p>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <span>Powered by</span>
            <img src="/quapp.png" alt="Quapp" className="h-5 object-contain" />
          </div>
        </div>
      </div>
    </div>
  )
}
